import { formatMessage, type MessageValues } from "../i18n/catalogUtils";
import {
  translateShared,
  type SharedMessageKey,
} from "../i18n/sharedCatalog";
import {
  BUILT_IN_SOURCE_LOCALE,
  type InstallableLocale,
} from "../i18n/preparedLocales";

export type DataStateKind =
  | "loading"
  | "loaded"
  | "empty"
  | "no-results"
  | "error"
  | "no-access"
  | "unavailable"
  | "partial"
  | "stale";

export type FeedbackTone = "neutral" | "info" | "success" | "warning" | "danger";

export type FeedbackAction = {
  label: string;
  onAction: () => void;
};

export type FeedbackState = {
  kind: Exclude<DataStateKind, "loaded">;
  title: string;
  message: string;
  titleKey?: SharedMessageKey;
  titleValues?: MessageValues;
  messageKey?: SharedMessageKey;
  messageValues?: MessageValues;
  tone: FeedbackTone;
  action?: FeedbackAction;
  announced?: boolean;
};

export class UserFacingError extends Error {
  readonly userCode?: string;
  readonly retryable: boolean;

  constructor(message: string, options: { code?: string; retryable?: boolean } = {}) {
    super(message);
    this.name = "UserFacingError";
    if (options.code !== undefined) this.userCode = options.code;
    this.retryable = options.retryable ?? false;
  }
}

export function toErrorPresentation(
  error: unknown,
  fallbackMessage = translateShared(BUILT_IN_SOURCE_LOCALE, "state.error.body"),
): FeedbackState & { kind: "error"; code?: string; retryable: boolean } {
  const safe = error instanceof UserFacingError ? error : null;
  return {
    kind: "error",
    title: translateShared(BUILT_IN_SOURCE_LOCALE, "state.error.title"),
    message: safe?.message ?? fallbackMessage,
    titleKey: "state.error.title",
    ...(!safe ? { messageKey: "state.error.body" as const } : {}),
    tone: "danger",
    ...(safe?.userCode ? { code: safe.userCode } : {}),
    retryable: safe?.retryable ?? false,
    announced: true,
  };
}

export function partialDataPresentation(hasTrustworthyData: boolean, error?: unknown): FeedbackState | null {
  if (!error) return null;
  if (!hasTrustworthyData) return toErrorPresentation(error);
  return {
    kind: "partial",
    title: translateShared(BUILT_IN_SOURCE_LOCALE, "state.partial.title"),
    message: translateShared(BUILT_IN_SOURCE_LOCALE, "state.partial.body"),
    titleKey: "state.partial.title",
    messageKey: "state.partial.body",
    tone: "warning",
    announced: true,
  };
}

export function staleDataPresentation(timestampLabel: string): FeedbackState {
  return {
    kind: "stale",
    title: translateShared(BUILT_IN_SOURCE_LOCALE, "state.stale.title"),
    message: formatMessage(
      translateShared(BUILT_IN_SOURCE_LOCALE, "state.stale.timestampBody"),
      { timestamp: timestampLabel },
    ),
    titleKey: "state.stale.title",
    messageKey: "state.stale.timestampBody",
    messageValues: { timestamp: timestampLabel },
    tone: "warning",
    announced: true,
  };
}


export function loadingPresentation(
  message = translateShared(BUILT_IN_SOURCE_LOCALE, "common.loading"),
): FeedbackState {
  return {
    kind: "loading",
    title: translateShared(BUILT_IN_SOURCE_LOCALE, "common.loading"),
    message,
    titleKey: "common.loading",
    tone: "info",
    announced: true,
  };
}

export function emptyPresentation(
  message = translateShared(BUILT_IN_SOURCE_LOCALE, "state.empty.body"),
): FeedbackState {
  return {
    kind: "empty",
    title: translateShared(BUILT_IN_SOURCE_LOCALE, "state.empty.title"),
    message,
    titleKey: "state.empty.title",
    ...(message === translateShared(BUILT_IN_SOURCE_LOCALE, "state.empty.body")
      ? { messageKey: "state.empty.body" as const }
      : {}),
    tone: "neutral",
  };
}

export function noResultsPresentation(
  message = translateShared(BUILT_IN_SOURCE_LOCALE, "state.noResults.body"),
): FeedbackState {
  return {
    kind: "no-results",
    title: translateShared(BUILT_IN_SOURCE_LOCALE, "state.noResults.title"),
    message,
    titleKey: "state.noResults.title",
    ...(message === translateShared(BUILT_IN_SOURCE_LOCALE, "state.noResults.body")
      ? { messageKey: "state.noResults.body" as const }
      : {}),
    tone: "neutral",
    announced: true,
  };
}

export function noAccessPresentation(
  message = translateShared(BUILT_IN_SOURCE_LOCALE, "state.noAccess.body"),
): FeedbackState {
  return {
    kind: "no-access",
    title: translateShared(BUILT_IN_SOURCE_LOCALE, "state.noAccess.title"),
    message,
    titleKey: "state.noAccess.title",
    ...(message === translateShared(BUILT_IN_SOURCE_LOCALE, "state.noAccess.body")
      ? { messageKey: "state.noAccess.body" as const }
      : {}),
    tone: "neutral",
  };
}

export function unavailablePresentation(
  message = translateShared(BUILT_IN_SOURCE_LOCALE, "state.unavailable.body"),
): FeedbackState {
  return {
    kind: "unavailable",
    title: translateShared(BUILT_IN_SOURCE_LOCALE, "state.unavailable.title"),
    message,
    titleKey: "state.unavailable.title",
    ...(message === translateShared(BUILT_IN_SOURCE_LOCALE, "state.unavailable.body")
      ? { messageKey: "state.unavailable.body" as const }
      : {}),
    tone: "neutral",
  };
}

export function localizeFeedbackState(
  locale: InstallableLocale,
  state: FeedbackState,
): FeedbackState {
  return {
    ...state,
    title: state.titleKey
      ? formatMessage(translateShared(locale, state.titleKey), state.titleValues)
      : state.title,
    message: state.messageKey
      ? formatMessage(translateShared(locale, state.messageKey), state.messageValues)
      : state.message,
  };
}
